import React, { useState, useMemo, useEffect } from "react"
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/card"
import { RadioGroup, RadioGroupItem } from "../components/radio-group"
import { Label } from "../components/label"
import { Button } from "../components/button"
import { Progress } from "../components/progress"
import Navbar from "../components/Navbar"
import Sidebar from "../components/Sidebar"
import TestResultUI from "../components/TestResultUI"
import { Lightbulb, Shield, Heart, Scale, Sparkles, ArrowRight } from 'lucide-react'
import api from "../sever/Api";
import { notifyTestCompletion } from "../lib/testUtils";

// Data for the quiz statements, transformed from the original questions
const rawQuizData = [
  {
    virtue: "Wisdom",
    icon: Lightbulb,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    strengths: ["Creativity", "Curiosity", "Judgment", "Love of Learning", "Perspective"],
    questions: [
      {
        id: "wisdom-q1",
        question: "You prefer solving a problem by:",
        options: [
          { text: "Thinking of new, unusual methods", strength: "Creativity" },
          { text: "Exploring as many angles as possible", strength: "Curiosity" },
          { text: "Weighing evidence before deciding", strength: "Judgment" },
          { text: "Drawing lessons from past experience", strength: "Love of Learning / Perspective" },
        ],
      },
      {
        id: "wisdom-q2",
        question: "In a conversation, you tend to:",
        options: [
          { text: "Imagine alternative possibilities", strength: "Creativity" },
          { text: "Ask deep, interesting questions", strength: "Curiosity" },
          { text: "Assess the credibility of claims", strength: "Judgment" },
          { text: "Share wisdom or advice from your broader view", strength: "Perspective" },
        ],
      },
      {
        id: "wisdom-q3",
        question: "Your version of fun is:",
        options: [
          { text: "Inventing something new", strength: "Creativity" },
          { text: "Discovering something unknown", strength: "Curiosity" },
          { text: "Debating ideas with logic", strength: "Judgment" },
          { text: "Absorbing a new subject deeply", strength: "Love of Learning" },
        ],
      },
      {
        id: "wisdom-q4",
        question: "When reading, you're drawn to:",
        options: [
          { text: "Fiction that sparks imagination", strength: "Creativity" },
          { text: "Non-fiction that broadens your worldview", strength: "Curiosity" },
          { text: "Critical books with balanced analysis", strength: "Judgment" },
          { text: "Educational resources for growth", strength: "Love of Learning" },
        ],
      },
      {
        id: "wisdom-q5",
        question: "Others say of you:",
        options: [
          { text: "You always think outside the box", strength: "Creativity" },
          { text: "You're always asking why", strength: "Curiosity" },
          { text: "You never rush a decision", strength: "Judgment" },
          { text: "You see the big picture", strength: "Perspective" },
        ],
      },
    ],
  },
  {
    virtue: "Courage",
    icon: Shield,
    color: "text-red-600",
    bgColor: "bg-red-50",
    strengths: ["Bravery", "Perseverance", "Honesty", "Zest"],
    questions: [
      {
        id: "courage-q1",
        question: "Under pressure, you are most likely to:",
        options: [
          { text: "Keep going no matter what", strength: "Perseverance" },
          { text: "Admit your feelings regardless of discomfort", strength: "Honesty" },
          { text: "Act even when you're frightened", strength: "Bravery" },
          { text: "Tackle challenges with boundless energy", strength: "Zest" },
        ],
      },
      {
        id: "courage-q2",
        question: "A typical description from others:",
        options: [
          { text: "You're fearless", strength: "Bravery" },
          { text: "You always finish what you start", strength: "Perseverance" },
          { text: "You say things as they are", strength: "Honesty" },
          { text: "You brighten any room", strength: "Zest" },
        ],
      },
      {
        id: "courage-q3",
        question: "In a new situation, you're likely to:",
        options: [
          { text: "Step up without hesitation", strength: "Bravery" },
          { text: "Stay committed even if it's tough", strength: "Perseverance" },
          { text: "Express your true thoughts", strength: "Honesty" },
          { text: "Dive in with enthusiasm", strength: "Zest" },
        ],
      },
      {
        id: "courage-q4",
        question: "When your values are challenged, you:",
        options: [
          { text: "Speak up anyway", strength: "Bravery" },
          { text: "Hold true to them through difficulty", strength: "Perseverance" },
          { text: "State your real position clearly", strength: "Honesty" },
          { text: "Do it with passion", strength: "Zest" },
        ],
      },
      {
        id: "courage-q5",
        question: "Your motivation comes from:",
        options: [
          { text: "Seeing risks but acting anyway", strength: "Bravery" },
          { text: "Completing goals no matter the obstacle", strength: "Perseverance" },
          { text: "Being authentic in every moment", strength: "Honesty" },
          { text: "Feeling alive with energy", strength: "Zest" },
        ],
      },
    ],
  },
  {
    virtue: "Humanity",
    icon: Heart,
    color: "text-pink-600",
    bgColor: "bg-pink-50",
    strengths: ["Love", "Kindness", "Social Intelligence"],
    questions: [
      {
        id: "humanity-q1",
        question: "When someone struggles, you:",
        options: [
          { text: "Do small acts of kindness", strength: "Kindness" },
          { text: "Offer a heartfelt hug or emotional presence", strength: "Love" },
          { text: "Sense what they need and adapt", strength: "Social Intelligence" },
          { text: "Encourage them to act", strength: "Kindness or Love" },
        ],
      },
      {
        id: "humanity-q2",
        question: "You build trust by:",
        options: [
          { text: "Listening and caring", strength: "Love" },
          { text: "Responding thoughtfully to emotions", strength: "Social Intelligence" },
          { text: "Doing something helpful without being asked", strength: "Kindness" },
          { text: "Maintaining a close, warm connection", strength: "Love" },
        ],
      },
      {
        id: "humanity-q3",
        question: "In a difficult group situation, you help by:",
        options: [
          { text: "Offering gentle support", strength: "Kindness" },
          { text: "Understanding unspoken dynamics", strength: "Social Intelligence" },
          { text: "Strengthening relationships through care", strength: "Love" },
          { text: "Acting thoughtfully and helping others connect", strength: "Social Intelligence" },
        ],
      },
      {
        id: "humanity-q4",
        question: "Which feels most natural?",
        options: [
          { text: "Doing favors without expecting anything", strength: "Kindness" },
          { text: "Creating deep bonds", strength: "Love" },
          { text: "Picking up on subtle emotional cues", strength: "Social Intelligence" },
          { text: "Encouraging harmony and warmth", strength: "Love" },
        ],
      },
      {
        id: "humanity-q5",
        question: "People describe you as:",
        options: [
          { text: "Compassionate and giving", strength: "Kindness" },
          { text: "Warm and close", strength: "Love" },
          { text: "Insightful about others", strength: "Social Intelligence" },
          { text: "Always ready to support", strength: "Kindness" },
        ],
      },
    ],
  },
  {
    virtue: "Justice",
    icon: Scale,
    color: "text-green-600",
    bgColor: "bg-green-50",
    strengths: ["Teamwork", "Fairness", "Leadership"],
    questions: [
      {
        id: "justice-q1",
        question: "In a team project, you:",
        options: [
          { text: "Ensure everyone's voice is heard", strength: "Fairness" },
          { text: "Coordinate tasks effectively", strength: "Teamwork" },
          { text: "Inspire and lead toward goals", strength: "Leadership" },
          { text: "Make sure outcomes are equitable", strength: "Fairness" },
        ],
      },
      {
        id: "justice-q2",
        question: "Others often call on you to:",
        options: [
          { text: "Keep things balanced and just", strength: "Fairness" },
          { text: "Bring everyone together", strength: "Teamwork" },
          { text: "Take the lead in organizing", strength: "Leadership" },
          { text: "Mediate disputes", strength: "Fairness or Teamwork" },
        ],
      },
      {
        id: "justice-q3",
        question: "You find satisfaction in:",
        options: [
          { text: "Leading toward a shared vision", strength: "Leadership" },
          { text: "Working collaboratively", strength: "Teamwork" },
          { text: "Making sure things are fair for all", strength: "Fairness" },
          { text: "Unifying diverse group members", strength: "Teamwork" },
        ],
      },
      {
        id: "justice-q4",
        question: "In disagreement, you:",
        options: [
          { text: "Evaluate each side evenly", strength: "Fairness" },
          { text: "Ensure collaborative dialogue", strength: "Teamwork" },
          { text: "Guide the group toward consensus", strength: "Leadership" },
          { text: "Help everyone feel included", strength: "Teamwork" },
        ],
      },
      {
        id: "justice-q5",
        question: "People say you are:",
        options: [
          { text: "A fair-minded person", strength: "Fairness" },
          { text: "A unifier who fosters cooperation", strength: "Teamwork" },
          { text: "A natural leader", strength: "Leadership" },
          { text: "Someone who promotes equal treatment", strength: "Fairness" },
        ],
      },
    ],
  },
  {
    virtue: "Transcendence",
    icon: Sparkles,
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    strengths: ["Appreciation of Beauty & Excellence", "Gratitude", "Hope", "Humor", "Spirituality"],
    questions: [
      {
        id: "transcendence-q1",
        question: "You feel uplifted when:",
        options: [
          { text: "You notice something beautiful in life", strength: "Appreciation of Beauty & Excellence" },
          { text: "You express thanks in any situation", strength: "Gratitude" },
          { text: "You think the future will be better", strength: "Hope" },
          { text: "You make others laugh during stress", strength: "Humor" },
        ],
      },
      {
        id: "transcendence-q2",
        question: "A satisfying moment is when:",
        options: [
          { text: "You pause to admire excellence around you", strength: "Appreciation of Beauty & Excellence" },
          { text: "You share what you're grateful for", strength: "Gratitude" },
          { text: "You maintain optimism in setbacks", strength: "Hope" },
          { text: "You lighten a heavy moment with humor", strength: "Humor" },
        ],
      },
      {
        id: "transcendence-q3",
        question: "People say you are:",
        options: [
          { text: "Someone who inspires hope", strength: "Hope" },
          { text: "Able to find humor in difficulty", strength: "Humor" },
          { text: "Grateful for everyday moments", strength: "Gratitude" },
          { text: "A lover of beauty and aesthetics", strength: "Appreciation of Beauty & Excellence" },
        ],
      },
      {
        id: "transcendence-q4",
        question: "When others are down, you offer:",
        options: [
          { text: "A hopeful outlook for their future", strength: "Hope" },
          { text: "A lighthearted joke or comment", strength: "Humor" },
          { text: "Insight into small perks to appreciate", strength: "Gratitude or Appreciation of Beauty & Excellence" },
          { text: "Reflection on something positive to value", strength: "Gratitude" },
        ],
      },
      {
        id: "transcendence-q5",
        question: "You reconnect with meaning by:",
        options: [
          { text: "Finding purpose beyond yourself", strength: "Spirituality or Hope" },
          { text: "Feeling thankful for life's gifts", strength: "Gratitude" },
          { text: "Seeing excellence in the world", strength: "Appreciation of Beauty & Excellence" },
          { text: "Experiencing moments of laughter and joy", strength: "Humor" },
        ],
      },
    ],
  },
]

// Flatten and normalize the quiz data into a single array of questions
const statements = []
let questionCounter = 0
rawQuizData.forEach((virtueBlock) => {
  virtueBlock.questions.forEach((questionBlock) => {
    statements.push({
      id: `question-${questionCounter++}`,
      question: questionBlock.question,
      text: questionBlock.options.map(option => option.text).join(' | '), // This will be used for display
      options: questionBlock.options,
      virtue: virtueBlock.virtue,
    })
  })
})

// Score mapping for the Likert scale options
const scoreMap = {
  "strongly-disagree": 1,
  disagree: 2,
  agree: 3,
  "strongly-agree": 4,
}

// Labels for the radio button options
const scoreLabels = {
  "strongly-disagree": "Strongly Disagree",
  disagree: "Disagree",
  agree: "Agree",
  "strongly-agree": "Strongly Agree",
}


export default function ViaCharacterStrengthTest() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [currentStatementIndex, setCurrentStatementIndex] = useState(0)
  const [answers, setAnswers] = useState({}) // Stores statementId -> selectedOptionValue
  const [showResults, setShowResults] = useState(false)
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // Showing concise summary only; detailed view removed per request
  const [showIntro, setShowIntro] = useState(true);
  const currentStatement = statements[currentStatementIndex]
  const isLastStatement = currentStatementIndex === statements.length - 1
  const totalStatements = statements.length
  const answeredStatements = Object.keys(answers).length
  const progressPercentage = Math.round((answeredStatements / totalStatements) * 100)
  const [autoNext, setAutoNext] = useState(false);
  const [latestTestResult, setLatestTestResult] = useState(null);

  // Calculate progress for each virtue
  const getVirtueProgress = (virtue) => {
    const virtueStatements = statements.filter(s => s.virtue === virtue)
    const answeredInVirtue = virtueStatements.filter(s => answers[s.id]).length
    return {
      answered: answeredInVirtue,
      total: virtueStatements.length
    }
  }

  // Handles user selecting an answer for the current statement
  const handleAnswerChange = (value) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [currentStatement.id]: value,
    }))
    setAutoNext(true);
  }

  // Moves to the next statement
  const handleNext = () => {
    if (currentStatementIndex < statements.length - 1) {
      setCurrentStatementIndex((prevIndex) => prevIndex + 1)
    }
  }

  const saveTestResult = async () => {
    try {
      // Use calculated results array
      const resultsArray = calculateResults;

      console.log(resultsArray, "resultsArray");

      // Build detailed answers
      const detailedAnswers = [];
      Object.entries(answers).forEach(([statementId, selectedOptionValue]) => {
        const statement = statements.find((s) => s.id === statementId);
        if (!statement) return;
        const selectedOption = statement.options[parseInt(selectedOptionValue)];
        const selectedOptionText = selectedOption ? selectedOption.text : "Not answered";
        detailedAnswers.push({
          questionId: statementId,
          questionText: statement.question,
          selectedOptionId: selectedOptionValue,
          selectedOptionText,
        });
      });

      // Calculate overall statistics
      const totalScore = resultsArray.reduce((sum, r) => sum + r.score, 0);
      const totalMaxScore = resultsArray.reduce((sum, r) => sum + r.maxScore, 0);
      const overallPercentage = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 100) : 0;

      // Convert scores to an object keyed by strength for easier reporting
      const scoresForSave = {};
      resultsArray.forEach((r) => {
        scoresForSave[r.strength] = {
          score: r.score,
          maxScore: r.maxScore,
          percentage: Math.round(r.percentage),
        };
      });

      // Save to the unified test-results endpoint so detailed report works in the dashboard
      await api.post("/api/test-results", {
        testType: "viacharacter",
        answers: detailedAnswers,
        scores: scoresForSave,
        overallScore: totalScore,
        overallPercentage,
      });

      toast.success("Test results saved successfully!");
    } catch (error) {
      console.error("Error saving test result:", error);
      toast.error(error.response?.data?.message || "Failed to save test results");
    }
  };

  // Save results when they are first shown
  useEffect(() => {
    if (showResults) {
      if (!paymentSuccess) {
        saveTestResult();
      }
      try {
        const resultsArray = calculateResults;
        const totalScore = resultsArray.reduce((sum, r) => sum + r.score, 0);
        const totalMaxScore = resultsArray.reduce((sum, r) => sum + r.maxScore, 0);
        const overallPercentage = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 100) : 0;
        const top = resultsArray[0];
        const testData = {
          scores: { personalityType: top?.strength || 'VIA Profile' },
          overallScore: overallPercentage,
          overallPercentage,
          testType: 'viacharacter',
          completed: true
        };
        notifyTestCompletion('viacharacter', testData);
      } catch (e) {
        // no-op
      }
    }
  }, [showResults]);

  // Check for showResults URL parameter
  useEffect(() => {


    async function getviaTestResult() {
      try {
        const response = await api.get("/api/test-results/via/last");
        const testResult = response.data?.lastTestResult;
        console.log(testResult, "testResult");

        setAnswers(testResult?.answers || {});



      } catch (error) {
        console.error("Error saving test result:", error);
      }
    };
    getviaTestResult();

    const params = new URLSearchParams(location.search);
    const showResultsParam = params.get('showResults');
    const paymentSuccess = params.get("reportCheckout")
    if (showResultsParam === 'true') {
      setShowResults(true);
    } else if (paymentSuccess === 'true' && showResultsParam === 'true') {
      setPaymentSuccess(true);
    }

  }, [location.search]);

  useEffect(() => {
    if (autoNext) {
      if (!isLastStatement) {
        handleNext();
      }
      setAutoNext(false);
    }
    // eslint-disable-next-line
  }, [autoNext]);

  // Submits the quiz and shows results
  const handleSubmit = () => {
    setShowResults(true)
  }

  // Calculates the scores for each strength based on user answers
  const calculateResults = useMemo(() => {
    const strengthScores = {}
    const strengthMaxScores = {}

    // Initialize all unique strengths and their max possible scores
    const allUniqueStrengths = Array.from(new Set(statements.flatMap((s) => s.options.flatMap(opt => opt.strength.split(' / ').flatMap(str => str.split(' or ')).map(str => str.trim())))))
    allUniqueStrengths.forEach((strength) => {
      strengthScores[strength] = 0
      strengthMaxScores[strength] = 0
    })

    console.log(statements, "statements");
    console.log(answers, "answers");

    // Calculate maximum possible scores for each strength based on how many times they appear
    statements.forEach((statement) => {
      statement.options.forEach((option) => {
        const strengths = option.strength.split(' / ').flatMap(str => str.split(' or ')).map(str => str.trim())
        strengths.forEach((strength) => {
          strengthMaxScores[strength] += 4 // Each option contributes a max of 4 points to its associated strength(s)
        })
      })
    })

    // Calculate actual scores based on user's answers
    Object.entries(answers).forEach(([statementId, selectedOptionValue]) => {
      const statement = statements.find((s) => s.id === statementId)
      if (statement && selectedOptionValue !== undefined) {
        const selectedOption = statement.options[parseInt(selectedOptionValue)]
        if (selectedOption) {
          const score = 4 - parseInt(selectedOptionValue) // Convert index (0,1,2,3) to score (4,3,2,1)
          const strengths = selectedOption.strength.split(' / ').flatMap(str => str.split(' or ')).map(str => str.trim())
          strengths.forEach((strength) => {
            strengthScores[strength] += score
          })
        }
      }
    })

    // Format results into an array of objects for easier rendering and sorting
    const resultsArray = Object.entries(strengthScores).map(([strength, score]) => ({
      strength,
      score,
      maxScore: strengthMaxScores[strength],
      percentage: (score / strengthMaxScores[strength]) * 100,
    }))

    // Sort results by percentage in descending order
    resultsArray.sort((a, b) => b.percentage - a.percentage)

    return resultsArray
  }, [answers]) // Recalculate only when answers change

  // Resets the quiz to its initial state
  const handleRetakeQuiz = () => {
    setCurrentStatementIndex(0)
    setAnswers({})
    setShowResults(false)
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Helper functions for detailed report
  const getStrengthDescription = (strength, percentage) => {
    switch (strength) {
      case "Creativity":
        return "Your creativity shines in how you imagine possibilities beyond the obvious. You don’t just solve problems — you reframe them, opening doors others didn’t even notice. In daily life, this shows up when you bring fresh ideas to work, cook with flair, or find playful ways to connect with loved ones. Creativity inside feels like curiosity and freedom; outside, it inspires others and sparks innovation. Studies show creative expression lowers stress and strengthens problem-solving. This strength is deeply connected with curiosity and love of learning, because your mind naturally wants to exploreand experiment";
      case "Curiosity":
        return "Your curiosity is that spark that makes you ask “Why?” and “What if?” It shows up when you dive into new books, explore different cultures, or simply wonder about how things work. Inside, curiosity keeps life exciting and ever-expanding; outside, it draws people to you because you make themfeel seen and interesting. Research shows curiosity boosts happiness,strengthens relationships, and even protects against anxiety. This strength intertwines with perspective and love of learning — your curiosity feeds your wisdom, and your wisdom, in turn, deepens your curiosity.";
      case "Judgment":
        return "Your judgment is your ability to think clearly and wisely. It shows up when you weigh different perspectives, gather evidence, and make balanced decisions. Internally, it gives you confidence in your choices; externally, people trust your counsel. Research shows sound judgment supports better problem-solving and resilience. This strength naturally connects with fairness and perspective, because your open-mindedness ensures your decisions are both wise and just.";
      case "Love of Learning":
        return "You don’t just like to know things — you thrive on the process of learning itself. Love of learning shows up when you dive into a new skill, explore ideas, or take joy in discovery. Inside, it fills you with excitement and growth; outside, it makes you a source of knowledge and inspiration for others. Research shows lifelong learning protects the brain, increases happiness, and expands resilience. This strength pairs beautifully with curiosity and perseverance — curiosity sparks your interest, perseverance carries you through, and learning enriches your whole journey";
      case "Perspective":
        return "You bring wisdom that helps others see the bigger picture. Perspective shows up when you calm a friend in crisis, or when you help reframe challenges in a way that feels hopeful. Internally, it gives you clarity and peace; externally, it offers guidance and reassurance to others. Studies show perspective is linked to better decision-making and well-being. It blends seamlessly with judgment, social intelligence, and hope, as you use insight not just for yourself but to uplift those around you.";
      case "Bravery":
        return "You have a way of stepping forward even when fear is present. Bravery isn’t just about grand acts — it’s in speaking up when silence would be easier, or choosing to be vulnerable in relationships. Internally, bravery gives you the dignity of living authentically; externally, it earns respect and encourages others to act courageously too. Research shows courage reduces regret and fosters resilience. This strength weaves naturally with honesty and leadership, because true bravery often means standing in your truth and guiding others with integrity.";
      case "Perseverance":
        return "I see in you a steady fire — the ability to keep moving forward when others might stop. Perseverance shows up when you finish tasks even after the excitement has faded, or when you keep learning from setbacks instead of letting them define you. Inside, it builds a quiet confidence: “I can do hard things.” Externally, others come to trust you as dependable, someone who follows through. Research shows that perseverance, often called “grit,” predicts long-term success and resilience. It pairs beautifully with hope and zest, because your ability to endure is fueled by a belief in better outcomes and the energy you bring to each day.";
      case "Honesty":
        return "Honesty is your commitment to living authentically. It shows up in telling the truth, keeping promises, and aligning your actions with your values. Inside, honesty gives you integrity and peace of mind; outside, it earns trust and credibility from others. Research shows honesty strengthens relationships and reduces stress, since you don’t carry the burden of pretense. Honesty pairs powerfully with bravery and fairness, because truth often requires courage and respect for justice.";
      case "Zest":
        return "You bring energy and enthusiasm to life. Zest shows up when you throw yourself fully into a project, savor a new experience, or simply greet the day with excitement. Inside, it fills you with vitality; outside, it inspires others to feel more alive too. Research shows zest is strongly linked with happiness, resilience, and even better physical health. Zest flows naturally with hope and perseverance, because your energy fuels your persistence and your belief in what’s possible.";
      case "Love":
        return "Love is at the core of how you relate to people. You value closeness, connection, and the give-and-take of deep relationships. Day to day, it’s in the way you check in on friends, show affection, or create a sense of belonging for those around you. Inside, love gives you warmth and emotional security; outside, it strengthens bonds, improves health, and creates lasting trust. Studies even show people who cultivate love experience greater happiness and longer life spans. Love pairs beautifully with kindness, social intelligence, and gratitude, because together they form the foundation of meaningful human connection.";
      case "Kindness":
        return "Kindness is the thread that runs through so much of how you live. It shows up in small acts — listening patiently, offering help, or speaking with warmth. Inside, kindness creates fulfillment and compassion; outside, it builds trust and goodwill in relationships. Research shows acts of kindness boost happiness for both giver and receiver, and even improve physical health. Kindness flows naturally with love, gratitude, and social intelligence, because all three keep your heart open to others.";
      case "Social Intelligence":
        return "You have a way of sensing what others are feeling and responding with care. Social intelligence shows up when you read a room, comfort a friend, or adapt to someone’s needs. Inside, it gives you empathy and understanding; outside, it helps you build meaningful and supportive connections. Studies show social intelligence predicts stronger relationships and leadership success. It links closely with kindness, perspective, and humor, since all are about tuning in to people and responding in thoughtful ways.";
      case "Teamwork":
        return "You have a gift for making people feel like they belong. Teamwork shows up when you collaborate, share credit, and look for ways to lift others up. Internally, it builds a sense of trust and connection; externally, it creates groups where everyone feels valued and included. Research tells us strong teamwork reduces stress and increases collective success. It blends beautifully with leadership, fairness, and kindness, because your style of working with others is both empowering and respectful.";
      case "Fairness":
        return "Fairness lives in your commitment to justice and balance. You notice when things feel unequal and instinctively want to restore harmony. In daily life, this shows up when you listen to all sides before making a decision, or when you ensure no one feels left out. Inside, fairness gives you peace of mind; outside, it builds trust and respect from others. Studies show fairness strengthens group cohesion and prevents conflict. It connects naturally with judgment and honesty, as your integrity and thoughtful decision-making are grounded in fairness.";
      case "Leadership":
        return "Leadership is in the way you guide, inspire, and create space for others to thrive. It shows up when you step forward to organize, when you listen deeply, and when you empower people rather than control them. Inside, leadership gives you purpose and direction; outside, it creates trust, collaboration, and shared success. Research shows effective leadership improves team performance and well-being. It blends naturally with teamwork, fairness, and social intelligence, because true leadership is about lifting others as you lead.";
      case "Appreciation of Beauty & Excellence":
        return "You see beauty where others might rush past. Whether it’s a sunset, a piece of music, or someone’s hard work, you pause and let yourself be moved. Internally, this brings awe, inspiration, and joy; externally, it reminds others to slow down and value excellence in all forms. Research shows appreciation boosts positive emotions and even nurtures humility. This strength connects closely with gratitude, spirituality, and perspective — together, they create a life marked by wonder and deep meaning.";
      case "Gratitude":
        return "You have a way of noticing the blessings, both big and small. Gratitude shows up when you pause to savor a meal, thank a colleague, or feel moved by someone’s kindness. Inside, it creates joy and contentment; outside, it makes others feel valued and appreciated. Research consistently shows gratitude increases happiness, strengthens relationships, and even improves sleep and health. It weaves effortlessly with love, kindness, and spirituality — all reminding you that life is richer when you recognize its gifts.";
      case "Hope":
        return "Hope is your inner anchor. It’s the quiet but steady belief that things can get better, that tomorrow holds possibilities worth working toward. In daily life, it shows up when you encourage yourself or others, when you set goals, or when you look for silver linings. Inside, hope fosters resilience and optimism; outside, it energizes those around you to keep going too. Research shows hope predicts better mental health, achievement, and physical well-being. Hope naturally partners with perseverance and zest, giving your efforts both fuel and direction.";
      case "Humor":
        return "You bring lightness to heavy moments. Humor shows up when you make someone smile in a tough day, or when you laugh at yourself instead of taking things too seriously. Inside, it gives you resilience and joy; outside, it builds bonds and reduces tension. Studies show humor strengthens relationships and lowers stress hormones. This strength pairs beautifully with social intelligence and kindness, because your humor is never at someone’s expense — it’s about creating connection and relief.";
      case "Spirituality":
        return "You carry within you a sense that life has deeper meaning. Spirituality shows up in your values, your rituals, and the way you seek connection with something larger than yourself. Inside, it brings peace, purpose, and resilience; outside, it guides how you treat others with compassion and integrity. Research shows spirituality supports mental health, reduces stress, and strengthens community. It connects naturally with gratitude, hope, and appreciation of beauty, as all invite you to see life as part of a larger, meaningful whole.";
      default:
        return "This strength is not yet fully defined in our system. It's important to continue exploring and developing this area.";
    }
  };

  const getStrengthUsage = (strength) => {
    switch (strength) {
      case "Creativity":
        return ["Ideate and innovate in your field", "Solve complex problems with innovative approaches", "Generate new ideas and solutions"];
      case "Curiosity":
        return ["Seek out new knowledge and experiences", "Explore different perspectives and viewpoints", "Ask deep, meaningful questions"];
      case "Judgment":
        return ["Analyze situations critically", "Evaluate options objectively", "Make well-informed decisions"];
      case "Love of Learning":
        return ["Engage in lifelong learning", "Stay curious and open to new ideas", "Seek out new experiences"];
      case "Perspective":
        return ["Understand complex situations from multiple angles", "Consider different viewpoints", "See the big picture"];
      case "Bravery":
        return ["Face your fears and take calculated risks", "Stand up for what you believe in", "Take on challenges"];
      case "Perseverance":
        return ["Stay committed to your goals", "Overcome obstacles and setbacks", "Maintain focus in the face of adversity"];
      case "Honesty":
        return ["Be truthful and authentic", "Maintain ethical standards", "Build trust"];
      case "Zest":
        return ["Maintain enthusiasm and energy", "Find joy in new experiences", "Express your passion"];
      case "Love":
        return ["Connect emotionally with others", "Show empathy and compassion", "Create a positive environment"];
      case "Kindness":
        return ["Be helpful and caring", "Consider others' feelings", "Foster community"];
      case "Social Intelligence":
        return ["Understand and interact with people on an emotional level", "Communicate effectively", "Foster healthy relationships"];
      case "Teamwork":
        return ["Work effectively with others", "Collaborate to achieve goals", "Create a positive work environment"];
      case "Fairness":
        return ["Treat everyone fairly", "Maintain ethical decision-making", "Ensure justice"];
      case "Leadership":
        return ["Inspire and motivate others", "Lead towards a shared vision", "Create a positive work environment"];
      case "Appreciation of Beauty & Excellence":
        return ["Appreciate beauty and art", "Focus on quality and excellence", "Pay attention to details"];
      case "Gratitude":
        return ["Express thanks for good things", "Maintain a positive outlook", "Overcome challenges"];
      case "Hope":
        return ["Maintain optimism in the face of adversity", "Believe in a better future", "Achieve goals"];
      case "Humor":
        return ["Lighten difficult situations", "Create a positive and engaging environment", "Communicate effectively"];
      case "Spirituality":
        return ["Connect with something greater than yourself", "Find purpose and meaning", "Create a meaningful life"];
      default:
        return ["This strength is not yet fully defined in our system. It's important to continue exploring and developing this area."];
    }
  };

  const getStrengthDevelopment = (strength) => {
    switch (strength) {
      case "Creativity":
        return ["Practice divergent thinking", "Engage in creative problem-solving", "Experiment with new techniques"];
      case "Curiosity":
        return ["Read widely and explore diverse topics", "Ask questions about everything", "Seek out new experiences"];
      case "Judgment":
        return ["Critically analyze your own thinking", "Seek diverse perspectives", "Consider different viewpoints"];
      case "Love of Learning":
        return ["Set learning goals and track progress", "Read extensively", "Engage in continuous learning"];
      case "Perspective":
        return ["Practice empathy and understanding", "Seek out diverse experiences", "Consider different viewpoints"];
      case "Bravery":
        return ["Face your fears and take calculated risks", "Challenge yourself with new experiences", "Take on challenges"];
      case "Perseverance":
        return ["Set realistic goals and break them down", "Practice resilience", "Stay committed to your objectives"];
      case "Honesty":
        return ["Reflect on your own actions and words", "Seek feedback and be open to criticism", "Be transparent"];
      case "Zest":
        return ["Find new ways to express your enthusiasm", "Engage in activities that energize you", "Stay curious"];
      case "Love":
        return ["Practice active listening and empathy", "Show appreciation for others", "Create deep connections"];
      case "Kindness":
        return ["Practice random acts of kindness", "Be generous with your time and resources", "Foster community"];
      case "Social Intelligence":
        return ["Practice emotional intelligence", "Seek feedback on your interactions", "Consider others' feelings"];
      case "Teamwork":
        return ["Seek opportunities to collaborate", "Practice active listening", "Be a supportive team member"];
      case "Fairness":
        return ["Reflect on your own biases", "Seek diverse perspectives", "Consider different viewpoints"];
      case "Leadership":
        return ["Seek feedback on your leadership style", "Practice empathy and understanding", "Consider others' perspectives"];
      case "Appreciation of Beauty & Excellence":
        return ["Engage in art and beauty", "Focus on quality and excellence", "Pay attention to details"];
      case "Gratitude":
        return ["Practice gratitude daily", "Reflect on the good in your life", "Focus on the present"];
      case "Hope":
        return ["Practice optimism and positive thinking", "Focus on the present", "Believe in a better future"];
      case "Humor":
        return ["Practice finding humor in difficult situations", "Engage in activities that bring joy", "Stay curious"];
      case "Spirituality":
        return ["Practice mindfulness and meditation", "Seek a higher power or purpose", "Reflect on your values"];
      default:
        return ["This strength is not yet fully defined in our system. It's important to continue exploring and developing this area."];
    }
  };

  const getCareerApplications = (strength) => {
    switch (strength) {
      case "Creativity":
        return ["Artistic roles (e.g., painter, musician, designer)", "Innovative problem-solving roles (e.g., consultant, researcher)", "Creative writing (e.g., novelist, playwright)"];
      case "Curiosity":
        return ["Research and development roles (e.g., scientist, researcher, analyst)", "Educational roles (e.g., teacher, professor, tutor)", "Journalism and writing (e.g., journalist, writer, editor)"];
      case "Judgment":
        return ["Strategic planning roles (e.g., CEO, manager, consultant)", "Risk assessment roles (e.g., risk analyst, auditor, compliance officer)", "Decision-making roles (e.g., lawyer, judge, arbitrator)"];
      case "Love of Learning":
        return ["Educational roles (e.g., teacher, professor, researcher)", "Research and development roles (e.g., scientist, researcher, analyst)", "Continuous learning and development roles"];
      case "Perspective":
        return ["Leadership roles (e.g., CEO, manager, director)", "Strategic planning roles (e.g., consultant, strategist, analyst)", "Diplomacy and conflict resolution roles"];
      case "Bravery":
        return ["Entrepreneurial roles (e.g., startup founder, business owner)", "Leadership roles (e.g., CEO, manager, director)", "Risk-taking roles (e.g., trader, investor, adventurer)"];
      case "Perseverance":
        return ["Entrepreneurial roles (e.g., startup founder, business owner)", "Leadership roles (e.g., CEO, manager, director)", "Continuous learning and development roles"];
      case "Honesty":
        return ["Legal roles (e.g., lawyer, judge, arbitrator)", "Leadership roles (e.g., CEO, manager, director)", "Ethical consulting roles (e.g., consultant, auditor, compliance officer)"];
      case "Zest":
        return ["Creative roles (e.g., artist, designer, musician)", "Entrepreneurial roles (e.g., startup founder, business owner)", "Innovative problem-solving roles (e.g., consultant, researcher)"];
      case "Love":
        return ["Relationship roles (e.g., therapist, counselor, coach)", "Community roles (e.g., volunteer, community organizer, activist)", "Leadership roles (e.g., CEO, manager, director)"];
      case "Kindness":
        return ["Relationship roles (e.g., therapist, counselor, coach)", "Community roles (e.g., volunteer, community organizer, activist)", "Leadership roles (e.g., CEO, manager, director)"];
      case "Social Intelligence":
        return ["Leadership roles (e.g., CEO, manager, director)", "Relationship roles (e.g., therapist, counselor, coach)", "Community roles (e.g., volunteer, community organizer, activist)"];
      case "Teamwork":
        return ["Leadership roles (e.g., CEO, manager, director)", "Project management roles (e.g., project manager, team lead, scrum master)", "Collaborative roles (e.g., consultant, analyst, researcher)"];
      case "Fairness":
        return ["Legal roles (e.g., lawyer, judge, arbitrator)", "Leadership roles (e.g., CEO, manager, director)", "Ethical consulting roles (e.g., consultant, auditor, compliance officer)"];
      case "Leadership":
        return ["Entrepreneurial roles (e.g., startup founder, business owner)", "Leadership roles (e.g., CEO, manager, director)", "Risk-taking roles (e.g., trader, investor, adventurer)"];
      case "Appreciation of Beauty & Excellence":
        return ["Artistic roles (e.g., painter, musician, designer)", "Creative writing (e.g., novelist, playwright)", "Innovative problem-solving roles (e.g., consultant, researcher)"];
      case "Gratitude":
        return ["Leadership roles (e.g., CEO, manager, director)", "Community roles (e.g., volunteer, community organizer, activist)", "Continuous learning and development roles"];
      case "Hope":
        return ["Leadership roles (e.g., CEO, manager, director)", "Community roles (e.g., volunteer, community organizer, activist)", "Continuous learning and development roles"];
      case "Humor":
        return ["Creative roles (e.g., artist, designer, musician)", "Entrepreneurial roles (e.g., startup founder, business owner)", "Innovative problem-solving roles (e.g., consultant, researcher)"];
      case "Spirituality":
        return ["Leadership roles (e.g., CEO, manager, director)", "Community roles (e.g., volunteer, community organizer, activist)", "Continuous learning and development roles"];
      default:
        return ["This strength is not yet fully defined in our system. It's important to continue exploring and developing this area."];
    }
  };

  const getRelationshipImpact = (strength) => {
    switch (strength) {
      case "Love":
        return "You have a deep capacity for love, empathy, and compassion. This strength is essential in building strong relationships, fostering community, and creating a positive environment. It's important to continue developing your ability to connect with others on an emotional level.";
      case "Kindness":
        return "You have a natural inclination to be helpful, caring, and considerate towards others. This strength is crucial in building trust, fostering community, and creating a supportive environment. It's important to continue developing your kindness and being generous in your actions.";
      case "Social Intelligence":
        return "You have a remarkable ability to understand and interact with people on an emotional level. This strength is essential in leadership, management, and roles that require empathy and communication. It's important to continue developing your emotional intelligence and being aware of others' feelings.";
      default:
        return "This strength is not yet fully defined in our system. It's important to continue exploring and developing this area.";
    }
  };

  const getPersonalDevelopment = (strength) => {
    switch (strength) {
      case "Creativity":
        return "You have a natural ability to generate novel ideas, think outside the box, and approach problems with innovative solutions. This strength can be particularly valuable in creative fields like art, design, or problem-solving roles. It's important to nurture this by engaging in activities that challenge your thinking and encourage divergent thinking.";
      case "Curiosity":
        return "You possess a strong desire to learn and explore new ideas, concepts, and experiences. This curiosity drives you to seek out knowledge and understanding, making you a lifelong learner. It's beneficial to channel this into areas that interest you, as it can lead to personal growth and professional development.";
      case "Judgment":
        return "You have a keen ability to analyze situations, evaluate options, and make well-informed decisions. This strength is crucial in leadership, management, and roles requiring strategic thinking. It's important to continue developing your critical thinking skills and remain open to new perspectives.";
      case "Love of Learning":
        return "You have a deep appreciation for knowledge and the pursuit of learning. This strength is particularly valuable in roles that require continuous education and adaptation. It's important to maintain your curiosity and seek out new experiences that broaden your understanding.";
      case "Perspective":
        return "You have a unique ability to understand and interpret complex situations from multiple viewpoints. This strength is invaluable in leadership, diplomacy, and roles that require empathy and understanding of diverse perspectives. It's important to continue developing your ability to see the big picture and consider different angles.";
      case "Bravery":
        return "You have the courage to face challenges, take risks, and stand up for what you believe in. This strength is essential in leadership, entrepreneurship, and roles that require resilience and determination. It's important to continue building your confidence and taking calculated risks.";
      case "Perseverance":
        return "You have the ability to persist in the face of obstacles and setbacks. This strength is crucial in achieving goals, overcoming challenges, and maintaining focus in the face of adversity. It's important to continue developing your resilience and staying committed to your objectives.";
      case "Honesty":
        return "You have the integrity to be truthful, authentic, and open in all your interactions. This strength is essential in building trust, maintaining ethical standards, and fostering healthy relationships. It's important to continue developing your honesty and being transparent in all aspects of your life.";
      case "Zest":
        return "You have a natural enthusiasm and energy for life. This strength is particularly valuable in roles that require creativity, innovation, and a positive outlook. It's important to continue nurturing your passion and finding ways to express your enthusiasm.";
      case "Love":
        return "You have a deep capacity for love, empathy, and compassion. This strength is essential in building strong relationships, fostering community, and creating a positive environment. It's important to continue developing your ability to connect with others on an emotional level.";
      case "Kindness":
        return "You have a natural inclination to be helpful, caring, and considerate towards others. This strength is crucial in building trust, fostering community, and creating a supportive environment. It's important to continue developing your kindness and being generous in your actions.";
      case "Social Intelligence":
        return "You have a remarkable ability to understand and interact with people on an emotional level. This strength is essential in leadership, management, and roles that require empathy and communication. It's important to continue developing your emotional intelligence and being aware of others' feelings.";
      case "Teamwork":
        return "You have a strong ability to collaborate, work effectively with others, and achieve common goals. This strength is crucial in achieving success, overcoming challenges, and creating a positive work environment. It's important to continue developing your teamwork skills and being a supportive team member.";
      case "Fairness":
        return "You have a strong commitment to justice, impartiality, and treating everyone fairly. This strength is essential in leadership, management, and roles that require ethical decision-making. It's important to continue developing your fairness and ensuring that all voices are heard.";
      case "Leadership":
        return "You have the ability to inspire, motivate, and lead others towards a shared vision. This strength is essential in achieving success, overcoming challenges, and creating a positive work environment. It's important to continue developing your leadership skills and being a role model.";
      case "Appreciation of Beauty & Excellence":
        return "You have a keen eye for beauty, art, and excellence in all aspects of life. This strength is particularly valuable in creative fields, leadership, and roles that require attention to detail and appreciation of quality. It's important to continue developing your appreciation for beauty and excellence.";
      case "Gratitude":
        return "You have a way of noticing the blessings, both big and small. Gratitude shows up when you pause to savor a meal, thank a colleague, or feel moved by someone’s kindness. Inside, it creates joy and contentment; outside, it makes others feel valued and appreciated. Research consistently shows gratitude increases happiness, strengthens relationships, and even improves sleep and health. It weaves effortlessly with love, kindness, and spirituality — all reminding you that life is richer when you recognize its gifts.";
      case "Hope":
        return "You have a strong belief in a better future and a positive outlook on life. This strength is essential in overcoming challenges, achieving goals, and creating a positive work environment. It's important to continue developing your hope and maintaining optimism in the face of adversity.";
      case "Humor":
        return "You have a natural ability to find humor in difficult situations and lighten the mood. This strength is particularly valuable in leadership, communication, and roles that require creativity and adaptability. It's important to continue developing your humor and using it to create a positive and engaging environment.";
      case "Spirituality":
        return "You have a strong connection to something greater than yourself, whether it's a belief in a higher power, a sense of purpose, or a deep appreciation for the world around you. This strength is essential in achieving personal growth, overcoming challenges, and creating a meaningful life.";
      default:
        return "This strength is not yet fully defined in our system. It's important to continue exploring and developing this area.";
    }
  };

  if (showIntro && !showResults) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] relative">
        {/* Sidebar */}
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar />
        </div>

        {/* Main content */}
        <div className="ml-64">
          <Navbar />

          <div className="w-full max-w-7xl mx-auto pt-10 pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
              <Card className="text-center bg-white shadow-lg">
                <CardHeader className="space-y-4">
                  <CardTitle className="text-3xl font-bold">
                    VIA Character Strengths Test
                  </CardTitle>
                  <CardDescription className="text-lg text-gray-600">
                    Identify what you naturally do well and the virtues you embody —
                    build confidence and clarity through this self-discovery test.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Categories highlight */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-left">
                    {rawQuizData.map((virtue, i) => (
                      <div key={i} className="space-y-1">
                        <h3 className={`font-semibold ${virtue.color}`}>
                          {virtue.virtue}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {virtue.description || "Explore this character strength"}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Info box */}
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>{statements.length} questions</strong>  • Gain insights into your top strengths
                    </p>
                  </div>

                  {/* Start Button */}
                  <Button
                    onClick={() => setShowIntro(false)}
                    className="w-full text-lg py-6 bg-purple-600 hover:bg-purple-700"
                    size="lg"
                  >
                    Start Test <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
      {/* Sidebar */}
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      </div>
      <div className="ml-0 md:ml-64 min-h-screen">
        <Navbar />
        {!showResults ? (
          <div className=" mx-auto p-4" style={{ width: '85%' }}>
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">VIA Character Strengths Test</h1>
              <p className="text-gray-600 max-w-2xl mx-auto">Identify what participants naturally do well and what virtues they embody—building confidence and clarity.</p>
            </div>

            {/* Progress by Category */}
            <div className="mb-8 bg-white rounded-lg border border-gray-200 pt-6 px-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Your Progress by Category</h2>
              <div className="grid grid-cols-5 gap-8 mb-8">
                {rawQuizData.map((virtue, index) => {
                  const progress = getVirtueProgress(virtue.virtue)
                  const Icon = virtue.icon

                  return (
                    <div
                      key={virtue.virtue}
                      className="text-center cursor-pointer transition-all duration-200 hover:opacity-80"
                    >
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3">
                        <Icon className={`w-8 h-8 ${virtue.color}`} />
                      </div>
                      <h3 className="font-semibold text-sm mb-1 text-gray-900">
                        {virtue.virtue}
                      </h3>
                      <div className="text-sm text-gray-500">
                        {progress.answered}/{progress.total}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="mb-5">
              {/* Progress Bar */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Question {currentStatementIndex + 1} of {totalStatements}
                </span>
                <span className="text-sm font-medium text-gray-700">
                  {progressPercentage}% Complete
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>

            <Card className=" mx-auto bg-[#ffffff]">
              <CardHeader className="border-b">
                <div className="flex items-center space-x-3 ">
                  <div>
                    <CardTitle className="text-xl">
                      {currentStatement.question}
                    </CardTitle>
                    <CardDescription>
                      {(() => {
                        const currentVirtue = rawQuizData.find(v => v.virtue === currentStatement.virtue);
                        const currentQuestionIndex = Math.floor(currentStatementIndex / 5);
                        const questionNumber = (currentQuestionIndex % 5) + 1;
                        return `${currentVirtue.virtue} - Question ${questionNumber}`;
                      })()}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6 pt-5">
                  <RadioGroup
                    value={answers[currentStatement.id] || ""}
                    onValueChange={handleAnswerChange}
                    className="grid gap-4"
                  >
                    {currentStatement.options.map((option, index) => (
                      <div
                        key={index}
                        onClick={() => handleAnswerChange(index.toString())}
                        className={`flex items-center rounded-lg border p-2 px-4 cursor-pointer transition-colors duration-200 ${answers[currentStatement.id] === index.toString()
                          ? "border-primary ring-2 ring-primary/50 bg-primary/5 dark:bg-primary/10"
                          : "hover:bg-[rgb(197,199,203)] dark:hover:bg-[rgb(150,150,150)]"
                          }`}
                      >
                        <label htmlFor={`option-${index}`} className="w-full cursor-pointer text-base">
                          {option.text}
                        </label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>
            <CardFooter className="flex justify-between mt-5">
              <Button
                variant="outline"
                onClick={() => setCurrentStatementIndex((prev) => Math.max(0, prev - 1))}
                disabled={currentStatementIndex === 0}
                className='bg-[#ffffff] px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none border border-gray-300 text-gray-700 bg-white hover:bg-gray-100'
              >
                Previous
              </Button>
              {/* {!isLastStatement && (
                <Button
                  className='bg-purple-600 hover:bg-purple-700 text-white flex items-center space-x-2'
                  onClick={handleNext}
                  disabled={!answers[currentStatement.id]}
                >
                  Next Question
                </Button>
              )} */}
              {isLastStatement && (
                <Button
                  className='bg-purple-600 hover:bg-purple-700 text-white flex items-center space-x-2'
                  onClick={handleSubmit}
                  disabled={!answers[currentStatement.id]}
                >
                  View Results
                </Button>
              )}
            </CardFooter>
          </div>
        ) : (
          <TestResultUI
            testTitle="Character Strengths"
            testDescription="Your VIA Character Strengths assessment results"
            results={
              <div className="space-y-6">
                <div className="grid gap-4">
                  {calculateResults.slice(0, 5).map((result) => (
                    <div key={result.strength} className="space-y-2 bg-[#f7f7f7f7] p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{result.strength}</span>
                      </div>
                      <Progress
                        value={result.percentage}
                        className="h-3"
                        label={`${result.score} / ${result.maxScore} (${result.percentage.toFixed(0)}%)`}
                      />
                      <p className="text-sm text-gray-700">
                        {getStrengthDescription(result.strength, result.percentage)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            }
            onRetake={handleRetakeQuiz}
            onBack={() => { }}
            showBackButton={false}
            aiReportData={{
              testType: 'viacharacter',
              // Send compact scores to the AI endpoint to improve reliability
              scores: calculateResults.reduce((acc, r) => {
                acc[r.strength] = Math.round(r.percentage);
                return acc;
              }, {}),
              answers: Object.entries(answers).map(([statementId, selectedOptionValue]) => {
                const statement = statements.find((s) => s.id === statementId);
                const idx = parseInt(selectedOptionValue);
                const selected = statement?.options[idx];
                return {
                  questionId: statementId,
                  questionText: statement?.question,
                  selectedOptionId: selected ? String(idx) : 'n/a',
                  selectedOptionText: selected ? selected.text : 'n/a'
                };
              }),
              overallScore: calculateResults.reduce((sum, r) => sum + r.score, 0),
              overallPercentage: (() => {
                const totalScore = calculateResults.reduce((sum, r) => sum + r.score, 0);
                const totalMax = calculateResults.reduce((sum, r) => sum + r.maxScore, 0);
                return totalMax ? Math.round((totalScore / totalMax) * 100) : 0;
              })()
            }}
          >
            {calculateResults.map((result) => (
              <Card className='bg-[#ffffff]' key={result.strength}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{result.strength}</CardTitle>
                      <CardDescription>Character strength assessment result</CardDescription>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold">{result.score}/{result.maxScore}</div>
                      <span className="px-3 py-1 rounded-full text-sm text-white bg-purple-500">{result.percentage.toFixed(0)}%</span>
                    </div>
                  </div>
                  <Progress
                    value={result.percentage}
                    className="h-3"
                    label={`${result.score}/${result.maxScore} (${result.percentage.toFixed(0)}%)`}
                  />
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700">{getStrengthDescription(result.strength, result.percentage)}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TestResultUI>
        )}
      </div>
    </div>
  )
} 